import React from "react";
import { Label } from "@/components/ui/label";
import { CIRCA_BRAND_DEFAULTS } from "@/lib/brandColors";
import {
  DEFAULT_COMMUNITY_GUIDELINES,
  communityThemeModeFromForm,
} from "@/lib/communityTheme";
import BrandColorField from "./BrandColorField";

const BRAND_FIELDS = [
  {
    key: "brand_primary",
    label: "Primary",
    hint: "Community header band and Join / primary buttons and links.",
  },
  {
    key: "brand_secondary",
    label: "Secondary",
    hint: "Page canvas and top bar. Sets light vs dark only when Secondary (or Primary if Secondary is empty) is quite dark — mid-bright blues stay light. Leave empty for a light Circa-like page. Side nav stays Circa Social.",
  },
  {
    key: "brand_accent",
    label: "Accent",
    hint: "Thin top stripe on the header and highlight details.",
  },
];

/**
 * Color pickers + guidelines textarea for community settings.
 * `value` keys: brand_primary, brand_secondary, brand_accent, guidelines
 */
export default function CommunityThemeFields({
  value,
  onChange,
  disabled = false,
  idPrefix = "community",
}) {
  const form = value || {};
  const setField = (key, next) =>
    onChange?.({ ...form, [key]: next });
  const pageMode = communityThemeModeFromForm(form);

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div>
          <p className="text-sm font-medium">Community colors</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Make this community look distinct — solid header and page color.
            Secondary sets light vs dark from the community&apos;s own colors
            only. Leave empty to inherit organization / Circa hues on a light
            page.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Preview page tone:{" "}
            <span className="font-medium text-foreground">
              {pageMode === "dark" ? "Dark page" : "Light page"}
            </span>
          </p>
        </div>
        {BRAND_FIELDS.map(({ key, label, hint }) => (
          <div key={key} className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-${key}`}>{label}</Label>
            <p className="text-xs text-muted-foreground">{hint}</p>
            <BrandColorField
              id={`${idPrefix}-${key}`}
              label={label}
              value={form[key] || ""}
              placeholderKey={key}
              placeholder={CIRCA_BRAND_DEFAULTS[key]}
              disabled={disabled}
              onChange={(next) => setField(key, next)}
            />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-guidelines`}>Guidelines</Label>
        <p className="text-xs text-muted-foreground">
          Shown in the community sidebar. Leave empty to use the default
          guidelines until you save a custom version.
        </p>
        <textarea
          id={`${idPrefix}-guidelines`}
          rows={6}
          value={form.guidelines ?? ""}
          placeholder={DEFAULT_COMMUNITY_GUIDELINES}
          disabled={disabled}
          maxLength={10000}
          onChange={(e) => setField("guidelines", e.target.value)}
          className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
        />
      </div>
    </div>
  );
}
